import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import '../global-polyfill';
import { useLatest } from '../src';

export function useStateQueryString<T extends string = string>(key: string, defaultValue?: T | null): [T | null, Dispatch<SetStateAction<T | null>>] {
	const initial = new URLSearchParams(window.location.search);

	const [value, setValue] = useState<T | null>((initial.get(key) as T | null) ?? defaultValue ?? null);

	// Sync the search params to the address bar and state when navigation changes them.
	useEffect(() => {
		const handleHistoryChange = () => {
			const search = new URLSearchParams(window.location.search);
			const urlValue = (search.get(key) as T | null) ?? defaultValue ?? null;
			setValue(currentValue => (currentValue === urlValue ? currentValue : urlValue));
		};

		const search = new URLSearchParams(window.location.search);
		if (defaultValue === value || !value) search.delete(key);
		else if (value) search.set(key, value);

		const qs = search.toString();
		const updated = window.location.pathname + (qs ? '?' + qs : '');
		if (window.location.pathname + window.location.search !== updated) {
			window.history.replaceState(null, '', updated);
		}

		window.addEventListener('app-history-change', handleHistoryChange);
		window.addEventListener('popstate', handleHistoryChange);

		return () => {
			window.removeEventListener('app-history-change', handleHistoryChange);
			window.removeEventListener('popstate', handleHistoryChange);
		};
	}, [key, defaultValue, value]);

	return [value ?? null, setValue];
}

/**
 * Function to use useStateQueryString hook and treat values as a string array
 * Serializes array as comma-separated values in the query string
 * @param key - String for picking up from url params
 * @param defaultValue - Array of strings - default array to set value if it is null
 * @returns [value, setValue] - array value and setter function that accepts an array
 */
export function useStateQueryStringArray<T extends string[] = string[]>(
	key: string,
	defaultValue?: T | null
): [T | null, Dispatch<SetStateAction<T | null>>] {
	const initial = new URLSearchParams(window.location.search);
	const initialValue = initial.get(key);
	const parsedInitial = initialValue ? (initialValue.split(',').filter(Boolean) as T) : (defaultValue ?? null);

	const [value, setValue] = useState<T | null>(parsedInitial);
	const latest = useLatest(value);

	// URL changed - update state
	useEffect(() => {
		const handleHistoryChange = () => {
			const search = new URLSearchParams(window.location.search);
			const urlValue = search.get(key);
			const parsedUrlValue = urlValue ? (urlValue.split(',').filter(Boolean) as T) : (defaultValue ?? null);

			const stateStr = latest.current?.join(',') || '';
			const urlStr = parsedUrlValue?.join(',') || '';

			if (urlStr !== stateStr) {
				setValue(parsedUrlValue);
			}
		};

		// Run on mount and when dependencies change
		handleHistoryChange();

		// Listen for custom history changes
		window.addEventListener('app-history-change', handleHistoryChange);
		window.addEventListener('popstate', handleHistoryChange);

		// Cleanup
		return () => {
			window.removeEventListener('app-history-change', handleHistoryChange);
			window.removeEventListener('popstate', handleHistoryChange);
		};
	}, [key, defaultValue]);

	const updateHistoryFromValue = useCallback(
		(nextValue: T | null) => {
			setValue(nextValue);

			const search = new URLSearchParams(window.location.search);
			search.delete(key);

			if (nextValue && nextValue.length > 0) {
				search.set(key, nextValue.join(','));
			}

			const qs = search.toString();
			const newUrl = location.pathname + (qs ? '?' + qs : '');
			if (window.location.pathname + window.location.search !== newUrl) {
				window.history.pushState(null, '', newUrl);
			}
		},
		[key, location.pathname, location.search]
	);

	const setValueWithHistory = useCallback(
		(next: SetStateAction<T | null>) => {
			setValue(previousValue => {
				const resolvedValue = typeof next === 'function' ? (next as (prev: T | null) => T | null)(previousValue) : next;
				updateHistoryFromValue(resolvedValue);
				return resolvedValue;
			});
		},
		[updateHistoryFromValue]
	);

	return [value ?? null, setValueWithHistory];
}

/** Enables a simpler query string mapping
 *
 * @example
 * Pass in a map of key to value for type T allowing you to deal with exactly what you want, whereas querystring deals only in strings
 *
 * ```ts
 * const [isAddDrawerOpen, setIsAddDrawerOpen] = useStateQueryStringMap(SUBSCRIPTION_QUERY_KEY, { add: true }, false);
 * ```
 */
export function useStateQueryStringMap<T>(key: string, map: Record<string, T>, defaultValue: T): [T, Dispatch<SetStateAction<T | null>>] {
	const [value, setValue] = useStateQueryString(key);

	return [value ? map[value] : defaultValue, (v: T | null) => setValue(Object.entries(map).find(f => f[1] === v)?.[0] ?? null)];
}

/**
 * Function to use useStateQueryString hook and treat all values as a Number
 * key - String for picking up from url params. Ex,: 'step' - url - 'www.test.com/?page=1321&step=1`
 * defaultValue - Number - default number to set value if it is null
 * map - optional Record<string, number> to map string values to number values
 * @returns - [value, setValue] = state update func, to get a Number original value and set Number as a state update
 */
export function useStateQueryStringToNumber(
	key: string,
	defaultValue: number,
	map?: Record<string, number>
): [number, Dispatch<SetStateAction<number | null>>] {
	const [value, setValue] = useStateQueryString(key);
	// todo make a context that enables detecting and nesting of this information

	const numberValue = value ? (map?.[value] ?? Number(value)) : 0;

	return [numberValue ?? defaultValue, (v: number | null) => setValue(String(v))];
}
